import './AssessmentStyle.css';

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Header } from '../../shared/headerNavigation/header/Header';
import Modal from '../../shared/modal/Modal';
import { getAssessmentPageTitle } from '../../shared/util/pageTitles';
import { ValidationProvider } from '../validation/ValidationProvider';
import { AssessmentFormContainer } from './AssessmentFormContainer';
import { AssessmentVideoContainer } from './AssessmentVideoContainer';
import { MobileOnlyMessage } from './MobileOnlyMessage';
import { useAssessment } from './useAssessment';

type ModalProps = {
  questionTitle: string;
  modalText?: string | React.ReactNode;
  main?: string | React.ReactNode;
  secondary?: unknown[];
};

type ModalTextContent =
  string | React.ReactNode | { main?: React.ReactNode; secondary?: unknown[] };

function AssessmentInner(props: Record<string, unknown>) {
  const { t } = useTranslation();
  const {
    assessmentValues,
    errorSummary,
    isSaveError,
    clearSaveError,
    mobileOnly,
    isPlatformMismatch,
    isRetakeEnabled,
    isSkipHealthAssessment,
    pageOptions,
    regionContent,
    repeatAssessment,
    navigateNextPageHandler,
    navigatePrevPageHandler,
    quickNavHandler,
    saveAssessmentValue,
  } = useAssessment(props);

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const [modalTextContent, setModalTextContent] = useState<ModalTextContent>();
  const [modalQuestionTitle, setModalQuestionTitle] = useState<string>('');

  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, []);

  const openModal = useCallback(({ modalText, main, secondary, questionTitle }: ModalProps) => {
    setModalIsOpen(true);
    setModalTextContent(modalText || { main, secondary });
    setModalQuestionTitle(questionTitle);
  }, []);

  if (mobileOnly) {
    return <MobileOnlyMessage />;
  }

  return (
    <>
      {modalIsOpen && (
        <Modal
          modalIsOpen={modalIsOpen}
          onModalClose={modalClickHandler}
          modalText={modalTextContent}
          questionTitle={modalQuestionTitle}
        />
      )}
      {isSaveError && (
        <Modal
          modalIsOpen={isSaveError}
          onModalClose={clearSaveError}
          modalText={t('assessment_error_save_failed')}
          questionTitle={t('assessment_error_save_failed_title')}
        />
      )}
      <div id="main_container" className="assessment">
        <Header
          page="assessment"
          progress={pageOptions.assessmentProgress}
          onNextPage={quickNavHandler}
          showSideDrawer
          type="transparent"
          assessmentPage={getAssessmentPageTitle(pageOptions.currentPage)}
        />

        <AssessmentVideoContainer pageOptions={pageOptions} />

        <AssessmentFormContainer
          errorSummary={errorSummary}
          pageOptions={pageOptions}
          isPlatformMismatch={isPlatformMismatch}
          isRetakeEnabled={isRetakeEnabled}
          isSkipHealthAssessment={isSkipHealthAssessment}
          repeatAssessment={repeatAssessment}
          assessmentValues={assessmentValues}
          saveAssessmentValue={saveAssessmentValue}
          openModal={openModal}
          regionContent={regionContent}
          navigateNextPageHandler={navigateNextPageHandler}
          navigatePrevPageHandler={navigatePrevPageHandler}
        />
      </div>
    </>
  );
}

export function Assessment(props: Record<string, unknown>) {
  return (
    <ValidationProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading -- wrapper only adds the validation provider, forwards route props to AssessmentInner */}
      <AssessmentInner {...props} />
    </ValidationProvider>
  );
}
